import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { useCart } from '@/context/CartContext';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { Trash2, Plus, Minus, ShoppingBag, Calendar, Clock, Truck, Gift, Pencil, Check, X } from 'lucide-react';
import { GiftHamperDialog } from '@/components/GiftHamperDialog';
import { HamperOptionsDialog } from '@/components/HamperOptionsDialog';
import { 
  calculateDefaultDeliveryDate, 
  getMinDeliveryDate, 
  formatDeliveryDate, 
  formatDateForInput,
  getDeliveryMessage,
  calculateHamperDeliveryDate,
  DELIVERY_TIME_SLOT 
} from '@/utils/deliveryDateUtils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HAMPER_IMAGES = {
  'smallPVC-hamper':       '/images/products/smallPVC-hamper.jpg',
  'kraftbox-hamper':       '/images/products/kraftbox-hamper.jpg',
  'base8-hamper':          '/images/products/base8-hamper.jpg',
  'base10-hamper':         '/images/products/base10-hamper.jpg',
  'pvc-medium-hamper':     '/images/products/pvc-medium-hamper.jpg',
  'corrugated-box-hamper': '/images/products/corrugated-box-hamper.jpg',
};
const DEFAULT_HAMPER_IMG = 'https://images.pexels.com/photos/28769885/pexels-photo-28769885.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940';

const Cart = () => {
  const { cart, updateCart, fetchCart, removeHamperFromCart, updateHamperQuantity } = useCart();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState(formatDateForInput(calculateDefaultDeliveryDate()));
  const [deliveryInfo, setDeliveryInfo] = useState(getDeliveryMessage());

  // Hamper edit state
  const [editingHamper, setEditingHamper] = useState(null);
  const [loadingHamperEdit, setLoadingHamperEdit] = useState(false);

  // Regular product inline qty edit
  const [editingProductId, setEditingProductId] = useState(null);
  const [editQty, setEditQty] = useState(1);

  // Delivery confirmation (required when hampers in cart)
  const [deliveryConfirmed, setDeliveryConfirmed] = useState(false);

  // Derived: what types of items are in the cart
  const hasHamperItems = cart?.items?.some(i => i.item_type === 'gift_hamper');
  const hasRegularItems = cart?.items?.some(i => i.item_type !== 'gift_hamper');
  const hamperDeliveryDate = calculateHamperDeliveryDate();

  const handleUpdateQuantity = async (productId, newQuantity) => {
    if (newQuantity < 1) {
      await updateCart(productId, 0);
      toast.success('Item removed from cart');
    } else {
      await updateCart(productId, newQuantity);
    }
  };

  const handleUpdateHamperQuantity = async (hamperCartId, newQty) => {
    if (newQty < 1) {
      await removeHamperFromCart(hamperCartId);
      toast.success('Gift hamper removed from cart');
    } else {
      await updateHamperQuantity(hamperCartId, newQty);
    }
  };

  const handleRemoveHamper = async (hamperCartId) => {
    await removeHamperFromCart(hamperCartId);
    toast.success('Gift hamper removed from cart');
  };

  const handleEditHamper = async (hamperItem) => {
    setLoadingHamperEdit(true);
    try {
      // Try hamper_options first (new 3-level structure), fall back to gift_hampers
      let config = null;
      let isNewStyle = false;
      try {
        const res = await axios.get(`${API}/hamper-options/${hamperItem.hamper_id}`);
        config = res.data;
        isNewStyle = true;
      } catch {
        const res = await axios.get(`${API}/gift-hampers/${hamperItem.hamper_id}`);
        config = res.data;
      }
      const initialSelections = {};
      hamperItem.selections?.forEach(s => { initialSelections[s.group_id] = s.option_id; });
      setEditingHamper({ hamperItem, hamperConfig: config, initialSelections, isNewStyle });
    } catch {
      toast.error('Failed to load hamper details');
    } finally {
      setLoadingHamperEdit(false);
    }
  };

  const handleStartQtyEdit = (productId, currentQty) => {
    setEditingProductId(productId);
    setEditQty(currentQty);
  };

  const handleSaveQtyEdit = async (productId) => {
    const qty = parseInt(editQty, 10);
    if (!qty || qty < 1) { toast.error('Enter a valid quantity'); return; }
    await updateCart(productId, qty);
    setEditingProductId(null);
    toast.success('Quantity updated');
  };

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      toast.error('Please login to view cart');
      navigate('/login');
      return;
    }
    fetchCart();
  }, [user, authLoading]);

  useEffect(() => {
    const interval = setInterval(() => {
      setDeliveryInfo(getDeliveryMessage());
      setSelectedDate(formatDateForInput(calculateDefaultDeliveryDate()));
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  const handleDateChange = (e) => {
    const newDate = new Date(e.target.value);
    if (newDate.getDay() === 0) {
      toast.error('Sorry, we are closed on Sundays. Please select another date.');
      return;
    }
    const minDate = getMinDeliveryDate();
    minDate.setHours(0, 0, 0, 0);
    newDate.setHours(0, 0, 0, 0);
    if (newDate < minDate) {
      toast.error('Please select a valid delivery date.');
      return;
    }
    setSelectedDate(e.target.value);
  };

  const handleCheckout = () => {
    if (cart?.items?.length === 0) {
      toast.error('Your cart is empty');
      return;
    }
    if (hasHamperItems && !deliveryConfirmed) {
      toast.error('Please confirm the delivery dates before proceeding.');
      return;
    }
    sessionStorage.setItem('selectedDeliveryDate', hasRegularItems ? selectedDate : '');
    if (hasHamperItems) {
      sessionStorage.setItem('hamperDeliveryDate', formatDateForInput(hamperDeliveryDate));
    } else {
      sessionStorage.removeItem('hamperDeliveryDate');
    }
    navigate('/checkout');
  };

  if (!user) return null;

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      <div className="flex-1 bg-gradient-to-b from-amber-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <h1 className="text-3xl md:text-4xl font-bold text-accent mb-8" data-testid="cart-title">
            Shopping Cart
          </h1>

          {cart?.items?.length === 0 ? (
            <Card className="p-12 text-center" data-testid="empty-cart">
              <ShoppingBag className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
              <h2 className="text-2xl font-semibold text-accent mb-2">Your cart is empty</h2>
              <p className="text-muted-foreground mb-6">Add some delicious items to get started!</p>
              <Button
                onClick={() => navigate('/products')}
                className="rounded-full bg-primary hover:bg-primary/90"
                data-testid="continue-shopping-button"
              >
                Continue Shopping
              </Button>
            </Card>
          ) : (
            <div className="grid lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2 space-y-4" data-testid="cart-items">
                {cart.items.map((item) => {
                  // Gift Hamper item
                  if (item.item_type === 'gift_hamper') {
                    return (
                      <Card key={item.hamper_cart_id} className="p-6 border-amber-200" data-testid={`cart-hamper-${item.hamper_cart_id}`}>
                        <div className="flex gap-4">
                          <div className="w-24 h-24 rounded-lg overflow-hidden flex-shrink-0 border border-amber-100 bg-amber-50">
                            <img
                              src={HAMPER_IMAGES[item.hamper_id] || DEFAULT_HAMPER_IMG}
                              alt={item.hamper_name}
                              className="w-full h-full object-cover"
                              onError={e => { e.target.src = DEFAULT_HAMPER_IMG; }}
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="font-semibold text-accent text-lg">{item.hamper_name}</h3>
                              <Badge className="bg-amber-100 text-amber-700 border-amber-200 text-xs">Gift Hamper</Badge>
                            </div>
                            <div className="flex flex-wrap gap-1 mb-2">
                              {item.selections?.map((s) => (
                                <span key={s.group_id} className="text-xs bg-gray-50 border border-gray-200 text-gray-600 px-2 py-0.5 rounded-full">
                                  {s.option_name}
                                </span>
                              ))}
                            </div>
                            <p className="text-lg font-bold text-primary">₹{item.unit_price}</p>
                          </div>
                          <div className="flex flex-col items-end justify-between gap-3">
                            <div className="flex items-center gap-1">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleEditHamper(item)}
                                disabled={loadingHamperEdit}
                                title="Edit hamper selections"
                                data-testid={`edit-hamper-${item.hamper_cart_id}`}
                              >
                                <Pencil className="h-4 w-4 text-amber-600" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleRemoveHamper(item.hamper_cart_id)}
                                data-testid={`remove-hamper-${item.hamper_cart_id}`}
                              >
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            </div>
                            <div className="flex items-center space-x-2">
                              <Button
                                variant="outline"
                                size="icon"
                                className="h-8 w-8 rounded-full"
                                onClick={() => handleUpdateHamperQuantity(item.hamper_cart_id, item.quantity - 1)}
                                data-testid={`decrease-hamper-quantity-${item.hamper_cart_id}`}
                              >
                                <Minus className="h-3 w-3" />
                              </Button>
                              <span className="text-sm font-semibold w-8 text-center" data-testid={`hamper-quantity-${item.hamper_cart_id}`}>
                                {item.quantity}
                              </span>
                              <Button
                                variant="outline"
                                size="icon"
                                className="h-8 w-8 rounded-full"
                                onClick={() => handleUpdateHamperQuantity(item.hamper_cart_id, item.quantity + 1)}
                                data-testid={`increase-hamper-quantity-${item.hamper_cart_id}`}
                              >
                                <Plus className="h-3 w-3" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      </Card>
                    );
                  }
                  // Regular product item
                  return (
                  <Card key={item.product_id} className="p-6" data-testid={`cart-item-${item.product_id}`}>
                    <div className="flex gap-4">
                      <img
                        src={item.product?.image}
                        alt={item.product?.name}
                        className="w-24 h-24 object-cover rounded-lg"
                        data-testid={`cart-item-image-${item.product_id}`}
                      />
                      
                      <div className="flex-1">
                        <h3 className="font-semibold text-accent text-lg" data-testid={`cart-item-name-${item.product_id}`}>
                          {item.product?.name}
                        </h3>
                        {item.product?.weight && (
                          <p className="text-sm text-muted-foreground">{item.product.weight}</p>
                        )}
                        <p className="text-lg font-bold text-primary mt-2" data-testid={`cart-item-price-${item.product_id}`}>
                          ₹{item.product?.price}
                        </p>
                      </div>

                      <div className="flex flex-col items-end justify-between">
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleStartQtyEdit(item.product_id, item.quantity)}
                            title="Edit quantity"
                            data-testid={`edit-item-${item.product_id}`}
                          >
                            <Pencil className="h-4 w-4 text-muted-foreground hover:text-accent" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleUpdateQuantity(item.product_id, 0)}
                            data-testid={`remove-item-${item.product_id}`}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>

                        {editingProductId === item.product_id ? (
                          <div className="flex items-center gap-1" data-testid={`qty-edit-${item.product_id}`}>
                            <Input
                              type="number"
                              value={editQty}
                              onChange={e => setEditQty(e.target.value)}
                              onKeyDown={e => e.key === 'Enter' && handleSaveQtyEdit(item.product_id)}
                              className="w-14 h-8 text-center text-sm px-1"
                              min={1}
                              autoFocus
                              data-testid={`qty-input-${item.product_id}`}
                            />
                            <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => handleSaveQtyEdit(item.product_id)} data-testid={`save-qty-${item.product_id}`}>
                              <Check className="h-3.5 w-3.5 text-green-600" />
                            </Button>
                            <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setEditingProductId(null)} data-testid={`cancel-qty-${item.product_id}`}>
                              <X className="h-3.5 w-3.5 text-destructive" />
                            </Button>
                          </div>
                        ) : (
                          <div className="flex items-center space-x-2">
                            <Button
                              variant="outline"
                              size="icon"
                              className="h-8 w-8 rounded-full"
                              onClick={() => handleUpdateQuantity(item.product_id, item.quantity - 1)}
                              data-testid={`decrease-quantity-${item.product_id}`}
                            >
                              <Minus className="h-3 w-3" />
                            </Button>
                            <span className="text-sm font-semibold w-8 text-center" data-testid={`cart-item-quantity-${item.product_id}`}>
                              {item.quantity}
                            </span>
                            <Button
                              variant="outline"
                              size="icon"
                              className="h-8 w-8 rounded-full"
                              onClick={() => handleUpdateQuantity(item.product_id, item.quantity + 1)}
                              data-testid={`increase-quantity-${item.product_id}`}
                            >
                              <Plus className="h-3 w-3" />
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  </Card>
                  );
                })}
              </div>

              <div className="lg:col-span-1 space-y-4">
                {/* Delivery Schedule Card */}
                <Card className="p-6 border-2 border-primary/20 bg-amber-50/50" data-testid="delivery-date-card">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <Calendar className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-accent">Delivery Schedule</h3>
                      <p className="text-xs text-muted-foreground">{DELIVERY_TIME_SLOT}</p>
                    </div>
                  </div>

                  {/* Regular items section */}
                  {hasRegularItems && (
                    <div className="mb-4">
                      <div className="flex items-center gap-1.5 text-xs font-semibold text-blue-700 mb-2">
                        <Truck className="h-3.5 w-3.5" /> Regular Bakery Items
                      </div>
                      <div className={`p-3 rounded-lg mb-3 ${deliveryInfo.type === 'same-day' ? 'bg-green-50 border border-green-200' : 'bg-blue-50 border border-blue-200'}`}>
                        <p className={`text-xs font-medium ${deliveryInfo.type === 'same-day' ? 'text-green-700' : 'text-blue-700'}`}>
                          {deliveryInfo.message}
                        </p>
                        {deliveryInfo.timeRemaining && (
                          <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                            <Clock className="h-3 w-3" />{deliveryInfo.timeRemaining}
                          </p>
                        )}
                      </div>
                      <Label className="text-xs text-muted-foreground">Select Delivery Date</Label>
                      <div className="relative mt-1">
                        <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground z-10" />
                        <Input
                          type="date"
                          value={selectedDate}
                          onChange={handleDateChange}
                          min={formatDateForInput(getMinDeliveryDate())}
                          className="pl-10 w-full text-sm"
                          data-testid="delivery-date-input"
                        />
                      </div>
                      <p className="text-xs text-muted-foreground mt-1.5">
                        {formatDeliveryDate(new Date(selectedDate))}
                      </p>
                      <p className="text-xs text-amber-600 mt-0.5">⚠️ Closed on Sundays</p>
                    </div>
                  )}

                  {/* Hamper delivery section */}
                  {hasHamperItems && (
                    <div className={hasRegularItems ? 'pt-4 border-t border-amber-200' : ''}>
                      <div className="flex items-center gap-1.5 text-xs font-semibold text-amber-700 mb-2">
                        <Gift className="h-3.5 w-3.5" /> Gift Hampers
                      </div>
                      <div className="bg-amber-50 border-2 border-amber-300 rounded-xl p-4" data-testid="hamper-delivery-info">
                        <div className="flex items-start gap-3">
                          <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <Gift className="h-4 w-4 text-primary" />
                          </div>
                          <div>
                            <p className="font-bold text-accent text-sm">
                              {formatDeliveryDate(hamperDeliveryDate)}
                            </p>
                            <p className="text-xs text-amber-700 mt-0.5">3 days from today — prepared fresh to order</p>
                          </div>
                        </div>
                      </div>

                      {/* Confirmation checkbox */}
                      <label
                        className={`flex items-start gap-3 mt-4 p-3 rounded-xl border-2 cursor-pointer transition-all ${
                          deliveryConfirmed
                            ? 'border-green-400 bg-green-50'
                            : 'border-amber-300 bg-white hover:bg-amber-50'
                        }`}
                        data-testid="delivery-confirm-label"
                      >
                        <input
                          type="checkbox"
                          checked={deliveryConfirmed}
                          onChange={e => setDeliveryConfirmed(e.target.checked)}
                          className="mt-0.5 accent-primary h-4 w-4 flex-shrink-0"
                          data-testid="delivery-confirm-checkbox"
                        />
                        <span className="text-xs text-muted-foreground leading-relaxed">
                          I've noted{' '}
                          {hasRegularItems ? (
                            <>
                              the delivery dates — regular items on{' '}
                              <strong className="text-accent">{formatDeliveryDate(new Date(selectedDate))}</strong>
                              {' '}and gift hampers on{' '}
                            </>
                          ) : (
                            <>my gift hampers will be delivered on </>
                          )}
                          <strong className="text-accent">{formatDeliveryDate(hamperDeliveryDate)}</strong>.
                        </span>
                      </label>
                    </div>
                  )}
                </Card>

                {/* Order Summary Card */}
                <Card className="p-6 sticky top-24" data-testid="cart-summary">
                  <h2 className="text-xl font-semibold text-accent mb-4">Order Summary</h2>
                  
                  <div className="space-y-3 mb-6">
                    <div className="flex justify-between text-muted-foreground">
                      <span>Subtotal</span>
                      <span data-testid="cart-subtotal">₹{cart.total?.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-muted-foreground">
                      <span>Delivery</span>
                      <span className="text-muted-foreground text-sm">Calculated at checkout</span>
                    </div>
                    <div className="border-t border-amber-100 pt-3 flex justify-between text-lg font-bold text-accent">
                      <span>Subtotal</span>
                      <span data-testid="cart-total">₹{cart.total?.toFixed(2)}</span>
                    </div>
                  </div>

                  <Button
                    className={`w-full h-12 rounded-full font-medium transition-all shadow-lg ${
                      hasHamperItems && !deliveryConfirmed
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : 'bg-primary hover:bg-primary/90 hover:scale-105 active:scale-95'
                    }`}
                    onClick={handleCheckout}
                    disabled={hasHamperItems && !deliveryConfirmed}
                    data-testid="checkout-button"
                  >
                    {hasHamperItems && !deliveryConfirmed
                      ? 'Confirm Delivery Dates Above to Proceed'
                      : 'Proceed to Checkout'}
                  </Button>
                  
                  {!hasHamperItems && (
                    <p className="text-xs text-center text-muted-foreground mt-3">
                      Delivery on {formatDeliveryDate(new Date(selectedDate))} • {DELIVERY_TIME_SLOT}
                    </p>
                  )}
                </Card>
              </div>
            </div>
          )}
        </div>
      </div>

      <Footer />

      {/* Edit Hamper Dialog — auto-selects new vs old style */}
      {editingHamper && (
        editingHamper.isNewStyle ? (
          <HamperOptionsDialog
            hamper={editingHamper.hamperConfig}
            open={!!editingHamper}
            onClose={() => setEditingHamper(null)}
            editMode={true}
            hamperCartId={editingHamper.hamperItem.hamper_cart_id}
            initialState={{ step: 2, variant: editingHamper.hamperConfig?.selection_groups?.[0]?.options?.find(v => v.id === editingHamper.hamperItem.selections?.[0]?.option_id), selections: editingHamper.initialSelections }}
          />
        ) : (
          <GiftHamperDialog
            hamper={editingHamper.hamperConfig}
            open={!!editingHamper}
            onClose={() => setEditingHamper(null)}
            editMode={true}
            hamperCartId={editingHamper.hamperItem.hamper_cart_id}
            initialSelections={editingHamper.initialSelections}
          />
        )
      )}
    </div>
  );
};

export default Cart;
